import { useState, useCallback, useRef, useEffect } from "react";
import { PdfCanvas } from "./PdfCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, Plus, Trash2, GripVertical, X, Check, MousePointer } from "lucide-react";

export interface FormField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
  d365Field?: string;
  page?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

interface Props {
  pdfUrl: string;
  fields: FormField[];
  onFieldsChange: (fields: FormField[]) => void;
  onClose: () => void;
  onSave: (fields: FormField[]) => void;
  saving?: boolean;
  language?: string;
}

const FIELD_TYPES = [
  { value: "text", label: "Text", labelPt: "Texto" },
  { value: "date", label: "Date", labelPt: "Data" },
  { value: "number", label: "Number", labelPt: "Número" },
  { value: "email", label: "Email", labelPt: "Email" },
  { value: "checkbox", label: "Checkbox", labelPt: "Checkbox" },
  { value: "dropdown", label: "Dropdown", labelPt: "Dropdown" },
];

const COLORS = ["#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

export function PdfFormEditor({ pdfUrl, fields, onFieldsChange, onClose, onSave, saving, language }: Props) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [placingMode, setPlacingMode] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pt = language === "pt";

  const pageFields = fields.filter(f => f.page === currentPage);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!placingMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = Math.max(2, Math.min(85, ((e.clientX - rect.left) / rect.width) * 100));
    const yPct = Math.max(2, Math.min(95, ((e.clientY - rect.top) / rect.height) * 100));

    const newField: FormField = {
      id: `f${Date.now().toString(36)}`,
      label: pt ? "Novo Campo" : "New Field",
      type: "text",
      required: true,
      page: currentPage,
      x: Math.round(xPct * 10) / 10,
      y: Math.round(yPct * 10) / 10,
      width: 30,
      height: 4,
    };
    const updated = [...fields, newField];
    onFieldsChange(updated);
    setSelectedIdx(updated.length - 1);
    setPlacingMode(false);
  }, [placingMode, currentPage, fields, onFieldsChange, pt]);

  // Drag handlers
  const startDrag = useCallback((idx: number, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setDragIdx(idx);
    const overlay = overlayRef.current;
    if (!overlay) return;

    const globalIdx = fields.findIndex(f => f === pageFields[idx]);
    const rect = overlay.getBoundingClientRect();

    const onMove = (ev: MouseEvent) => {
      const xPct = Math.max(2, Math.min(85, ((ev.clientX - rect.left) / rect.width) * 100));
      const yPct = Math.max(2, Math.min(95, ((ev.clientY - rect.top) / rect.height) * 100));
      const u = [...fields];
      u[globalIdx] = { ...u[globalIdx], x: Math.round(xPct * 10) / 10, y: Math.round(yPct * 10) / 10 };
      onFieldsChange(u);
    };
    const onUp = () => {
      setDragIdx(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [fields, pageFields, onFieldsChange]);

  const removeField = (globalIdx: number) => {
    onFieldsChange(fields.filter((_, i) => i !== globalIdx));
    setSelectedIdx(null);
  };

  const updateField = (globalIdx: number, updates: Partial<FormField>) => {
    const u = [...fields];
    u[globalIdx] = { ...u[globalIdx], ...updates };
    onFieldsChange(u);
  };

  const selectedField = selectedIdx !== null ? fields[selectedIdx] : null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> {pt ? "Fechar" : "Close"}
          </Button>
          <span className="text-sm font-medium">
            {pt ? "Editor de Campos" : "Field Editor"} — {pt ? "Página" : "Page"} {currentPage + 1}/{pageCount}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={placingMode ? "default" : "outline"} size="sm" onClick={() => setPlacingMode(!placingMode)}>
            {placingMode ? <><MousePointer className="h-4 w-4 mr-1" /> {pt ? "Clique no PDF..." : "Click on PDF..."}</> 
              : <><Plus className="h-4 w-4 mr-1" /> {pt ? "Adicionar Campo" : "Add Field"}</>}
          </Button>
          <Button size="sm" onClick={() => onSave(fields)} disabled={saving}>
            {saving ? <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" /> 
              : <Check className="h-4 w-4 mr-1" />}
            {pt ? "Salvar" : "Save"}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* PDF preview with overlay */}
        <div className="flex-1 overflow-auto bg-muted/50 p-4">
          {/* Page navigation */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <Button variant="outline" size="sm" disabled={currentPage <= 0}
              onClick={() => setCurrentPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm min-w-[80px] text-center">
              {currentPage + 1} / {pageCount}
            </span>
            <Button variant="outline" size="sm" disabled={currentPage >= pageCount - 1}
              onClick={() => setCurrentPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* PDF + field overlay */}
          <div className="max-w-3xl mx-auto relative shadow-lg rounded-lg overflow-hidden bg-white">
            <PdfCanvas
              src={pdfUrl}
              pageNumber={currentPage}
              onPageCount={setPageCount}
            />
            {/* Clickable overlay */}
            <div
              ref={overlayRef}
              className={`absolute inset-0 ${placingMode ? "cursor-crosshair" : ""}`}
              style={{ zIndex: 2 }}
              onClick={handleOverlayClick}
            >
              {pageFields.map((field, localIdx) => {
                const globalIdx = fields.indexOf(field);
                const color = COLORS[globalIdx % COLORS.length];
                const isSelected = selectedIdx === globalIdx;
                const isDragging = dragIdx === localIdx;
                return (
                  <div
                    key={field.id}
                    className="absolute group"
                    style={{
                      left: `${field.x ?? 10}%`,
                      top: `${field.y ?? 10}%`,
                      width: `${field.width ?? 30}%`,
                      minHeight: 24,
                      border: `2px ${isSelected ? "solid" : "dashed"} ${color}`,
                      background: `${color}15`,
                      borderRadius: 4,
                      cursor: isDragging ? "grabbing" : "grab",
                      transition: isDragging ? "none" : "box-shadow 0.15s",
                      boxShadow: isSelected ? `0 0 0 2px ${color}40` : "none",
                      zIndex: isSelected ? 20 : 10,
                    }}
                    onClick={(e) => { e.stopPropagation(); setSelectedIdx(globalIdx); }}
                    onMouseDown={(e) => startDrag(localIdx, e)}
                  >
                    <div className="flex items-center gap-1 px-1.5 py-0.5" style={{ pointerEvents: "none" }}>
                      <GripVertical className="h-3 w-3 opacity-50" style={{ color }} />
                      <span className="text-[10px] font-semibold truncate" style={{ color }}>
                        {field.label || "..."}{field.required ? " *" : ""}
                      </span>
                    </div>
                    {/* Delete button on hover */}
                    <button
                      className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ pointerEvents: "auto", zIndex: 30 }}
                      onClick={(e) => { e.stopPropagation(); removeField(globalIdx); }}
                    >×</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right panel — field properties */}
        <div className="w-72 border-l bg-background overflow-y-auto shrink-0 hidden sm:block">
          <div className="p-4">
            {selectedField ? (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center justify-between">
                  {pt ? "Propriedades" : "Properties"}
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedIdx(null)}>
                    <X className="h-3 w-3" />
                  </Button>
                </h3>
                <div className="space-y-1">
                  <Label className="text-xs">{pt ? "Rótulo" : "Label"}</Label>
                  <Input className="h-8 text-sm" value={selectedField.label}
                    onChange={e => updateField(selectedIdx!, { label: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{pt ? "Tipo" : "Type"}</Label>
                  <Select value={selectedField.type} onValueChange={v => updateField(selectedIdx!, { type: v })}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(ft => (
                        <SelectItem key={ft.value} value={ft.value}>{pt ? ft.labelPt : ft.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{pt ? "Campo D365" : "D365 Field"}</Label>
                  <Input className="h-8 text-xs font-mono" placeholder="address1_line1"
                    value={selectedField.d365Field || ""}
                    onChange={e => updateField(selectedIdx!, { d365Field: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{pt ? "Texto de exemplo" : "Placeholder"}</Label>
                  <Input className="h-8 text-xs" value={selectedField.placeholder || ""}
                    onChange={e => updateField(selectedIdx!, { placeholder: e.target.value })} />
                </div>
                {selectedField.type === "dropdown" && (
                  <div className="space-y-1">
                    <Label className="text-xs">{pt ? "Opções (vírgula)" : "Options (comma)"}</Label>
                    <Input className="h-8 text-xs" placeholder="Opt1, Opt2, Opt3"
                      value={(selectedField.options || []).join(", ")}
                      onChange={e => updateField(selectedIdx!, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Switch checked={selectedField.required}
                    onCheckedChange={v => updateField(selectedIdx!, { required: v })} />
                  <span className="text-xs">{pt ? "Obrigatório" : "Required"}</span>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-[10px] text-muted-foreground mb-1">
                    {pt ? "Posição" : "Position"}: x={selectedField.x?.toFixed(1)}%, y={selectedField.y?.toFixed(1)}%
                  </p>
                  <Button variant="destructive" size="sm" className="w-full" onClick={() => removeField(selectedIdx!)}>
                    <Trash2 className="h-3 w-3 mr-1" /> {pt ? "Remover" : "Remove"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <MousePointer className="h-8 w-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">{pt ? "Selecione um campo" : "Select a field"}</p>
                <p className="text-xs mt-1">
                  {pt 
                    ? 'Clique em um campo no PDF para editar, ou clique "Adicionar Campo" para posicionar um novo.'
                    : 'Click a field on the PDF to edit, or click "Add Field" to place a new one.'}
                </p>
                {fields.length > 0 && (
                  <div className="mt-4 text-left space-y-1">
                    <p className="text-xs font-medium text-foreground">{pt ? "Todos os campos:" : "All fields:"}</p>
                    {fields.map((f, i) => (
                      <button key={f.id}
                        className="w-full text-left text-xs px-2 py-1 rounded hover:bg-muted flex items-center gap-2"
                        onClick={() => { setSelectedIdx(i); if (f.page !== undefined) setCurrentPage(f.page); }}>
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="truncate">{f.label || "..."}</span>
                        <span className="text-muted-foreground ml-auto shrink-0">P{(f.page ?? 0) + 1}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
